import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import * as slider from '@zag-js/slider';
import { useMachine, normalizeProps } from '@zag-js/react';
import { BigNumber } from '@ethersproject/bignumber';
import { formatUnits, parseUnits } from '@ethersproject/units';
import { BIG_ZERO } from '../../../../constants';
import { DecoratedSilo, getCollateralAssets, SiloAsset } from 'queries/decoratedSilo';
import { calculateLTV, mapToCollateralAsset, mapToDebtAsset, reachHealthFactor } from 'app/features/util/ltv';
import produce from 'immer';
import { BaseHealthFactor } from './components';
import { PERCENT_DECIMALS } from '@silo/types';
import { useGrabbingCursor, useSyncValues } from '@silo/ui';
import { normalizeToPoints_LEGACY } from '@silo/utils';

export type NewVariantProps = {
  variant: 'new';
  normalizedTokenAmount: BigNumber | null;
  setTokenAmount: Dispatch<SetStateAction<string>>;
  decoratedSilo: DecoratedSilo;
  newDebtAsset: SiloAsset;
  disabled?: boolean;
};

export const NewVariant: FC<NewVariantProps> = ({
  normalizedTokenAmount,
  decoratedSilo,
  newDebtAsset,
  setTokenAmount,
  disabled = false,
}) => {
  const newLTV = useMemo(() => {
    const collateralAssets = getCollateralAssets(decoratedSilo);
    const debtBalance = normalizedTokenAmount || BIG_ZERO;
    const debtAsset = produce(newDebtAsset, (draft) => {
      draft.debtBalance = debtBalance;
    });

    return calculateLTV({
      collateralAssets: collateralAssets.map(mapToCollateralAsset),
      debtAssets: [mapToDebtAsset(debtAsset)],
    });
  }, [decoratedSilo, newDebtAsset, normalizedTokenAmount]);

  const newHealthFactorPoints = normalizeToPoints_LEGACY(newLTV.healthFactor);
  const riskFactorPoints = normalizeToPoints_LEGACY(newLTV.riskFactor);

  const setRiskFactorPoints = useCallback(
    (value: number) => {
      const collateralAssets = getCollateralAssets(decoratedSilo);

      // subtracting 2 since value is points vs. a percentage (i.e. `50` should be intepreted as `0.50`)
      const healthFactor = parseUnits(`${100 - value}`, PERCENT_DECIMALS - 2).toBigInt();

      const { tokens, decimals } = reachHealthFactor({
        collateralAssets: collateralAssets.map(mapToCollateralAsset),
        debtAssets: [mapToDebtAsset(newDebtAsset)],
        healthFactor,
        asset: newDebtAsset,
      });

      setTokenAmount(tokens === 0n ? '' : formatUnits(tokens, decimals));
    },
    [decoratedSilo, newDebtAsset, setTokenAmount]
  );

  return (
    <BaseHealthFactor
      displayPoints={newHealthFactorPoints}
      disabled={disabled}
      bar={
        <NewVariantSlider
          disabled={disabled}
          riskFactorPoints={riskFactorPoints}
          setRiskFactorPoints={setRiskFactorPoints}
        />
      }
    />
  );
};

interface NewVariantSliderProps {
  riskFactorPoints: number;
  setRiskFactorPoints: (value: number) => void;
  disabled?: boolean;
}

const NewVariantSlider: FC<NewVariantSliderProps> = ({ riskFactorPoints, setRiskFactorPoints, disabled = false }) => {
  const [state, send] = useMachine(slider.machine({ id: 'new-loan-slider' }), { context: { disabled } });
  const api = slider.connect(state, send, normalizeProps);

  useSyncValues({
    parentValue: riskFactorPoints,
    setParentValue: setRiskFactorPoints,
    transformParentValue: Math.round,
    localValue: api.value,
    setLocalValue: api.setValue,
    transformLocalValue: Math.round,
  });

  useGrabbingCursor(api.isDragging);

  return (
    <div className="pt-4" {...api.rootProps}>
      <div className="flex items-center" {...api.controlProps}>
        <div className="flex-1 h-2.5 health-gradient-bg rounded-full" {...api.trackProps} />
        <div
          className={` h-[18px] w-[18px] bg-slate-100 transition-[filter,background-color] hover:bg-white active:bg-white focus:bg-white hover:ease-in active:ease-in focus:ease-in duration-100 hover:drop-shadow-slider active:drop-shadow-slider focus:drop-shadow-slider rounded-full ${
            api.isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          {...api.thumbProps}
        >
          <input {...api.hiddenInputProps} />
        </div>
      </div>
    </div>
  );
};
